import { buildPartnersSeed, DEFAULT_PARTNERS_PAGE } from "./partnersSeed";

const CMS_KEY = "emicron_cms_v2";
const AUTH_KEY = "emicron_cms_auth";

const PARTNERS_SEED = buildPartnersSeed();

const DEFAULT_STATE = {
  blogCategories: [
    { id: "cat-cloud", name: "Cloud & AWS", slug: "cloud-aws" },
    { id: "cat-security", name: "Cybersecurity", slug: "cybersecurity" },
    { id: "cat-insights", name: "IT Insights", slug: "it-insights" },
  ],
  blogs: [
    {
      id: "blog-1",
      title: "How AWS Activate Credits Help Startups Scale Faster",
      slug: "aws-activate-credits-startups",
      categoryId: "cat-cloud",
      excerpt:
        "Learn how Emicron helps startups unlock AWS Activate credits and build cloud-ready products without heavy infrastructure cost.",
      content: `## Why early cloud spend matters
Startups often struggle with cloud costs in the early stage. AWS Activate provides credits that reduce infrastructure spend while teams focus on product, not invoices.

## How Emicron helps
At Emicron, we guide eligible startups through eligibility checks, documentation, and architecture recommendations so credits are used effectively — not burned on oversized environments.

## What a strong Activate plan includes
From compute and storage planning to security baselines, the right Activate setup can accelerate go-to-market timelines significantly.

We typically help founders with:
- Account structure and IAM foundations
- Cost tagging and budget alerts
- Landing-zone style baselines for growth
- Choosing the right services for the first 12–18 months

## Next step
If you are evaluating AWS Activate, talk to Emicron’s cloud team for a practical eligibility and architecture review before you apply.`,
      image: "/img/blog/blog-aws.svg",
      author: "Emicron Cloud Team",
      readTime: "6 min read",
      tags: "AWS Activate, Startups, Cloud Cost",
      publishedAt: "2026-06-12",
      status: "published",
    },
    {
      id: "blog-2",
      title: "5 Cybersecurity Essentials Every Growing Business Needs",
      slug: "cybersecurity-essentials-growing-business",
      categoryId: "cat-security",
      excerpt:
        "A practical checklist for endpoint protection, email security, backups, and access control as your team and data footprint grow.",
      content: `## Growth expands the attack surface
As businesses grow, attack surfaces expand — more devices, more users, more cloud apps, and more third-party integrations.

## 1. Identity and access
Priority one is identity and access management: strong authentication, MFA, and least-privilege access for every critical system.

## 2. Endpoint and email security
Next come endpoint protection and email security. Most breaches still start with a compromised device or a phishing message.

## 3. Backups you can actually restore
Encrypted, tested backups matter as much as firewalls. Recovery drills prove your RTO/RPO assumptions.

## 4. Network and cloud visibility
Segment critical systems, monitor unusual traffic, and keep cloud admin activity auditable.

## 5. Incident response
A clear incident response plan — who to call, what to isolate, how to communicate — turns a crisis into a managed event.

Emicron helps organizations implement these layers with proven partner technologies and operational playbooks.`,
      image: "/img/blog/blog-security.svg",
      author: "Emicron Security Team",
      readTime: "7 min read",
      tags: "Cybersecurity, Endpoint, Email Security",
      publishedAt: "2026-05-28",
      status: "published",
    },
    {
      id: "blog-3",
      title: "Managed IT vs In-House: What Indian SMEs Should Consider",
      slug: "managed-it-vs-in-house-sme",
      categoryId: "cat-insights",
      excerpt:
        "Compare cost, expertise, and uptime when choosing between building an internal IT team and partnering with a managed services provider.",
      content: `## The SME IT dilemma
Many SMEs start with a small internal IT team. As environments grow, specialized skills for networking, security, and cloud become harder to staff full-time.

## Where in-house still wins
Internal teams know your people and processes deeply. They are often better for day-to-day coordination when the stack is small and stable.

## Where managed IT helps
Managed IT services provide 24×7 monitoring, certified expertise, and predictable operating costs — especially useful when you need coverage across security, cloud, and service desk.

## A practical hybrid model
Emicron works as an extension of your team — covering service desk, infrastructure, and proactive maintenance so your staff can focus on business outcomes.

Ask yourself:
- Do we need specialty skills every week, or only during incidents?
- Can we afford 24×7 coverage with our current headcount?
- Is our downtime cost higher than a managed retainer?

The right answer is rarely all-or-nothing — it is usually a clear split of ownership.`,
      image: "/img/blog/blog-managed-it.svg",
      author: "Emicron Leadership",
      readTime: "5 min read",
      tags: "Managed IT, SME, Operations",
      publishedAt: "2026-04-15",
      status: "published",
    },
  ],
  jobCategories: [
    { id: "jobcat-eng", name: "Engineering", slug: "engineering" },
    { id: "jobcat-sales", name: "Sales & Pre-Sales", slug: "sales-pre-sales" },
    { id: "jobcat-ops", name: "Operations", slug: "operations" },
  ],
  jobs: [
    {
      id: "job-1",
      title: "Cloud Solutions Engineer",
      slug: "cloud-solutions-engineer",
      categoryId: "jobcat-eng",
      location: "New Delhi / Hybrid",
      type: "Full-time",
      experience: "2–5 years",
      salary: "₹8–14 LPA (negotiable)",
      department: "Cloud Delivery",
      openings: "2",
      description:
        "Design and implement AWS and hybrid cloud solutions for enterprise and startup customers. Work with architects and delivery teams on migrations, landing zones, and cost optimization.\n\nYou will own customer-facing technical outcomes — from discovery workshops to go-live support — and help Emicron scale cloud delivery quality across accounts.",
      responsibilities:
        "Design AWS architectures aligned to customer requirements\nImplement landing zones, networking, and IAM baselines\nSupport migrations, modernization, and cost-optimization engagements\nDocument runbooks and hand over to managed services teams\nCollaborate with pre-sales on POCs and solution proposals",
      requirements:
        "Hands-on AWS experience (EC2, VPC, IAM, S3, CloudWatch)\nStrong troubleshooting skills across Linux/Windows\nGood communication with customers and internal delivery teams\nScripting familiarity (Python/Bash) preferred\nAWS Associate-level certification is a plus",
      benefits:
        "Hybrid work flexibility\nLearning budget for AWS certifications\nHealth insurance\nPerformance-linked incentives\nExposure to enterprise and startup cloud projects",
      status: "open",
      postedAt: "2026-06-01",
    },
    {
      id: "job-2",
      title: "Cybersecurity Pre-Sales Consultant",
      slug: "cybersecurity-pre-sales-consultant",
      categoryId: "jobcat-sales",
      location: "NCR",
      type: "Full-time",
      experience: "3–6 years",
      salary: "Competitive + incentives",
      department: "Security Sales",
      openings: "1",
      description:
        "Support enterprise security opportunities with solution demos, POCs, and proposal preparation across endpoint, email, and network security portfolios.\n\nYou will partner with account managers to shape technical win strategies and help customers choose the right security stack for their risk profile.",
      responsibilities:
        "Lead technical discovery and solution mapping\nRun demos, POCs, and workshops for enterprise prospects\nPrepare BOMs, architecture diagrams, and proposal inputs\nCoordinate with OEMs and delivery for feasibility\nSupport RFP / RFI responses for security deals",
      requirements:
        "Experience in IT security sales engineering\nUnderstanding of SIEM / EDR / firewall / email security concepts\nAbility to run technical workshops confidently\nWillingness to travel within NCR\nStrong written English for proposals",
      benefits:
        "Incentive structure tied to closed deals\nOEM training pathways\nLaptop and travel support\nHigh-visibility enterprise customer exposure",
      status: "open",
      postedAt: "2026-05-20",
    },
    {
      id: "job-3",
      title: "Service Desk Executive",
      slug: "service-desk-executive",
      categoryId: "jobcat-ops",
      location: "New Delhi",
      type: "Full-time",
      experience: "1–3 years",
      salary: "As per industry standards",
      department: "Managed Services",
      openings: "3",
      description:
        "Handle L1/L2 tickets, coordinate with delivery teams, and ensure timely updates to customers through Emicron Service Desk processes.\n\nThis role is ideal if you enjoy structured support work, clear SLAs, and becoming the trusted first contact for Emicron customers.",
      responsibilities:
        "Triage and resolve L1 issues; escalate L2/L3 as needed\nUpdate ticket status and communicate clearly with customers\nFollow runbooks for common Windows, network, and M365 issues\nMaintain ticket quality, notes, and SLA discipline\nParticipate in shift handovers and knowledge-base updates",
      requirements:
        "ITIL awareness preferred\nStrong written and verbal English\nBasic networking / Windows / Office 365 knowledge\nShift flexibility for support coverage\nCustomer-first attitude under pressure",
      benefits:
        "Structured training on Emicron tools and processes\nClear growth path into L2 / delivery roles\nShift allowances as applicable\nStable enterprise customer portfolio",
      status: "open",
      postedAt: "2026-05-10",
    },
  ],
  applications: [],
  saleInquiries: [],
  partnersPage: PARTNERS_SEED.partnersPage,
  partnerCategories: PARTNERS_SEED.partnerCategories,
  partners: PARTNERS_SEED.partners,
};

function uid(prefix) {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

function readState() {
  try {
    const raw = localStorage.getItem(CMS_KEY);
    if (!raw) {
      localStorage.setItem(CMS_KEY, JSON.stringify(DEFAULT_STATE));
      return structuredClone(DEFAULT_STATE);
    }
    const parsed = JSON.parse(raw);
    return {
      blogCategories: parsed.blogCategories?.length ? parsed.blogCategories : DEFAULT_STATE.blogCategories,
      blogs: parsed.blogs?.length ? parsed.blogs : DEFAULT_STATE.blogs,
      jobCategories: parsed.jobCategories?.length ? parsed.jobCategories : DEFAULT_STATE.jobCategories,
      jobs: parsed.jobs?.length ? parsed.jobs : DEFAULT_STATE.jobs,
      applications: Array.isArray(parsed.applications) ? parsed.applications : [],
      saleInquiries: Array.isArray(parsed.saleInquiries) ? parsed.saleInquiries : [],
      partnersPage: { ...DEFAULT_PARTNERS_PAGE, ...(parsed.partnersPage || {}) },
      partnerCategories: parsed.partnerCategories?.length
        ? parsed.partnerCategories
        : DEFAULT_STATE.partnerCategories,
      partners: parsed.partners?.length ? parsed.partners : DEFAULT_STATE.partners,
    };
  } catch {
    return structuredClone(DEFAULT_STATE);
  }
}

function writeState(state) {
  localStorage.setItem(CMS_KEY, JSON.stringify(state));
  return state;
}

export function resetCmsToSeed() {
  return writeState(structuredClone(DEFAULT_STATE));
}

export function getCmsState() {
  return readState();
}

export function isCmsAuthed() {
  try {
    return localStorage.getItem(AUTH_KEY) === "1";
  } catch {
    return false;
  }
}

export function cmsLogin(username, password) {
  return false;
}

export function cmsLogout() {
  localStorage.removeItem(AUTH_KEY);
}

function slugify(text) {
  return String(text)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

/* Blog categories */
export function listBlogCategories() {
  return readState().blogCategories;
}

export function saveBlogCategory(payload) {
  const state = readState();
  if (payload.id) {
    state.blogCategories = state.blogCategories.map((c) =>
      c.id === payload.id ? { ...c, name: payload.name, slug: payload.slug || slugify(payload.name) } : c
    );
  } else {
    state.blogCategories.push({
      id: uid("cat"),
      name: payload.name,
      slug: payload.slug || slugify(payload.name),
    });
  }
  return writeState(state);
}

export function deleteBlogCategory(id) {
  const state = readState();
  state.blogCategories = state.blogCategories.filter((c) => c.id !== id);
  return writeState(state);
}

/* Blogs */
export function listBlogs() {
  return readState().blogs.sort((a, b) => String(b.publishedAt).localeCompare(String(a.publishedAt)));
}

export function getBlogBySlug(slug) {
  return readState().blogs.find((b) => b.slug === slug) || null;
}

export function getBlogById(id) {
  return readState().blogs.find((b) => b.id === id) || null;
}

export function saveBlog(payload) {
  const state = readState();
  const slug = payload.slug || slugify(payload.title);
  if (payload.id) {
    state.blogs = state.blogs.map((b) =>
      b.id === payload.id
        ? {
            ...b,
            ...payload,
            slug,
          }
        : b
    );
  } else {
    state.blogs.unshift({
      id: uid("blog"),
      title: payload.title,
      slug,
      categoryId: payload.categoryId,
      excerpt: payload.excerpt || "",
      content: payload.content || "",
      image: payload.image || "/img/blog/blog-aws.svg",
      author: payload.author || "Emicron Team",
      readTime: payload.readTime || "",
      tags: payload.tags || "",
      publishedAt: payload.publishedAt || new Date().toISOString().slice(0, 10),
      status: payload.status || "published",
    });
  }
  return writeState(state);
}

export function deleteBlog(id) {
  const state = readState();
  state.blogs = state.blogs.filter((b) => b.id !== id);
  return writeState(state);
}

/* Job categories */
export function listJobCategories() {
  return readState().jobCategories;
}

export function saveJobCategory(payload) {
  const state = readState();
  if (payload.id) {
    state.jobCategories = state.jobCategories.map((c) =>
      c.id === payload.id ? { ...c, name: payload.name, slug: payload.slug || slugify(payload.name) } : c
    );
  } else {
    state.jobCategories.push({
      id: uid("jobcat"),
      name: payload.name,
      slug: payload.slug || slugify(payload.name),
    });
  }
  return writeState(state);
}

export function deleteJobCategory(id) {
  const state = readState();
  state.jobCategories = state.jobCategories.filter((c) => c.id !== id);
  return writeState(state);
}

/* Jobs */
export function listJobs() {
  return readState().jobs.sort((a, b) => String(b.postedAt).localeCompare(String(a.postedAt)));
}

export function getJobBySlug(slug) {
  return readState().jobs.find((j) => j.slug === slug) || null;
}

export function getJobById(id) {
  return readState().jobs.find((j) => j.id === id) || null;
}

export function saveJob(payload) {
  const state = readState();
  const slug = payload.slug || slugify(payload.title);
  if (payload.id) {
    state.jobs = state.jobs.map((j) => (j.id === payload.id ? { ...j, ...payload, slug } : j));
  } else {
    state.jobs.unshift({
      id: uid("job"),
      title: payload.title,
      slug,
      categoryId: payload.categoryId,
      location: payload.location || "",
      type: payload.type || "Full-time",
      experience: payload.experience || "",
      salary: payload.salary || "",
      department: payload.department || "",
      openings: payload.openings || "",
      description: payload.description || "",
      responsibilities: payload.responsibilities || "",
      requirements: payload.requirements || "",
      benefits: payload.benefits || "",
      status: payload.status || "open",
      postedAt: payload.postedAt || new Date().toISOString().slice(0, 10),
    });
  }
  return writeState(state);
}

export function deleteJob(id) {
  const state = readState();
  state.jobs = state.jobs.filter((j) => j.id !== id);
  state.applications = state.applications.filter((a) => a.jobId !== id);
  return writeState(state);
}

/* Applications */
export function listApplications() {
  return readState().applications.sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
}

export function submitJobApplication(payload) {
  const state = readState();
  state.applications.unshift({
    id: uid("app"),
    jobId: payload.jobId,
    jobTitle: payload.jobTitle,
    name: payload.name,
    email: payload.email,
    phone: payload.phone || "",
    coverLetter: payload.coverLetter || "",
    resumeName: payload.resumeName || "",
    resumeDataUrl: payload.resumeDataUrl || "",
    createdAt: new Date().toISOString(),
    status: "new",
  });
  return writeState(state);
}

export function updateApplicationStatus(id, status) {
  const state = readState();
  state.applications = state.applications.map((a) => (a.id === id ? { ...a, status } : a));
  return writeState(state);
}

export function deleteApplication(id) {
  const state = readState();
  state.applications = state.applications.filter((a) => a.id !== id);
  return writeState(state);
}

/* Sale inquiries (chat / sales form — separate from service desk tickets) */
export function listSaleInquiries() {
  return [...(readState().saleInquiries || [])].sort((a, b) =>
    String(b.createdAt).localeCompare(String(a.createdAt))
  );
}

export function submitSaleInquiry(payload) {
  const state = readState();
  if (!Array.isArray(state.saleInquiries)) state.saleInquiries = [];
  state.saleInquiries.unshift({
    id: uid("sale"),
    name: payload.name || "",
    email: payload.email || "",
    company: payload.company || "",
    phone: payload.phone || "",
    interest: payload.interest || "Other",
    message: payload.message || "",
    source: payload.source || "chat",
    createdAt: new Date().toISOString(),
    status: "new",
  });
  return writeState(state);
}

export function updateSaleInquiryStatus(id, status) {
  const state = readState();
  state.saleInquiries = (state.saleInquiries || []).map((item) =>
    item.id === id ? { ...item, status } : item
  );
  return writeState(state);
}

export function deleteSaleInquiry(id) {
  const state = readState();
  state.saleInquiries = (state.saleInquiries || []).filter((item) => item.id !== id);
  return writeState(state);
}

/* Partners page copy */
export function getPartnersPage() {
  return readState().partnersPage || { ...DEFAULT_PARTNERS_PAGE };
}

export function savePartnersPage(payload) {
  const state = readState();
  state.partnersPage = { ...DEFAULT_PARTNERS_PAGE, ...state.partnersPage, ...payload };
  return writeState(state);
}

/* Partner categories */
export function listPartnerCategories() {
  return [...readState().partnerCategories].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

export function savePartnerCategory(payload) {
  const state = readState();
  if (payload.id) {
    state.partnerCategories = state.partnerCategories.map((c) =>
      c.id === payload.id
        ? {
            ...c,
            name: payload.name,
            slug: payload.slug || slugify(payload.name),
            folder: payload.folder ?? c.folder ?? "",
            sortOrder: Number(payload.sortOrder ?? c.sortOrder ?? 0),
          }
        : c
    );
  } else {
    state.partnerCategories.push({
      id: uid("pcat"),
      name: payload.name,
      slug: payload.slug || slugify(payload.name),
      folder: payload.folder || "",
      sortOrder: Number(payload.sortOrder ?? state.partnerCategories.length),
    });
  }
  return writeState(state);
}

export function deletePartnerCategory(id) {
  const state = readState();
  state.partnerCategories = state.partnerCategories.filter((c) => c.id !== id);
  state.partners = state.partners.filter((p) => p.categoryId !== id);
  return writeState(state);
}

/* Partners / logos */
export function listPartners() {
  return [...readState().partners].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

export function listPartnersByCategory(categoryId) {
  return listPartners().filter((p) => p.categoryId === categoryId && p.status !== "hidden");
}

export function savePartner(payload) {
  const state = readState();
  if (payload.id) {
    state.partners = state.partners.map((p) =>
      p.id === payload.id
        ? {
            ...p,
            ...payload,
            sortOrder: Number(payload.sortOrder ?? p.sortOrder ?? 0),
          }
        : p
    );
  } else {
    state.partners.push({
      id: uid("partner"),
      categoryId: payload.categoryId,
      name: payload.name,
      image: payload.image || "",
      link: payload.link || "",
      hoverText: payload.hoverText || "",
      sortOrder: Number(payload.sortOrder ?? state.partners.filter((x) => x.categoryId === payload.categoryId).length),
      status: payload.status || "active",
    });
  }
  return writeState(state);
}

export function deletePartner(id) {
  const state = readState();
  state.partners = state.partners.filter((p) => p.id !== id);
  return writeState(state);
}

export function getDashboardStats() {
  const s = readState();
  return {
    blogs: s.blogs.length,
    blogCategories: s.blogCategories.length,
    jobs: s.jobs.length,
    openJobs: s.jobs.filter((j) => j.status === "open").length,
    applications: s.applications.length,
    newApplications: s.applications.filter((a) => a.status === "new").length,
    saleInquiries: (s.saleInquiries || []).length,
    newSaleInquiries: (s.saleInquiries || []).filter((i) => i.status === "new").length,
    partners: s.partners.length,
    partnerCategories: s.partnerCategories.length,
  };
}

export { slugify };
